import { Checkbox } from '@/components/ui/checkbox';
import { ButtonCheckbox } from '@/components/ui/button-checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/tailwind-utils';
import { ComponentProps } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

type CheckboxPropsWithTestId = ComponentProps<typeof Checkbox> & {
  'data-testid'?: string;
};

interface FormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  checkboxProps?: CheckboxPropsWithTestId;
  labelTestId?: string;
  horizontal?: boolean;
  className?: string;
  CheckboxComponent?: typeof Checkbox | typeof ButtonCheckbox;
  secondaryLabel?: string | ((checked: boolean) => string | undefined);
  checkboxClassName?: string;
}

export function FormCheckbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  checkboxProps = {},
  labelTestId,
  horizontal = false,
  className = '',
  CheckboxComponent = Checkbox,
  secondaryLabel,
  checkboxClassName = '',
}: FormCheckboxProps<TFieldValues, TName>) {
  const { required, ...others } = checkboxProps;

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            horizontal ? 'flex flex-row-reverse items-center justify-end gap-2 space-y-0' : '',
            CheckboxComponent === ButtonCheckbox ? 'flex flex-col items-start' : '',
            className,
          )}
        >
          {horizontal && <FormMessage />}
          {CheckboxComponent !== ButtonCheckbox && (
            <FormLabel className={cn('font-bold', { 'text-gray-400': others.disabled })} data-testid={labelTestId}>
              {label}
              {required && '*'}
            </FormLabel>
          )}
          <div className={cn(horizontal && 'flex items-center')}>
            <FormControl>
              {CheckboxComponent === ButtonCheckbox ? (
                <ButtonCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  secondaryLabel={typeof secondaryLabel === 'function' ? secondaryLabel(field.value) : secondaryLabel}
                  className={checkboxClassName}
                  {...others}
                >
                  {label}
                </ButtonCheckbox>
              ) : (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid={others['data-testid']}
                  {...others}
                />
              )}
            </FormControl>
          </div>
          {!horizontal && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
